import { useLocation, useParams } from 'react-router';
import { Shield } from 'lucide-react';

import {
  BackLink,
  ContactValue,
  DetailSection,
  Field,
  FieldGrid,
  StatTile,
} from '@/components/admin/detail-parts';
import { StatusBadge } from '@/components/ui/status-badge';
import type { AdminOrganizationDetail } from '@/contracts';
import { useCityLabel } from '@/i18n/use-city-label';
import { useTranslations } from '@/i18n/use-locale';
import { isoDate, listReturnPath } from '@/lib/detail';
import { formatCount } from '@/lib/format';
import { useOrganizationKindLabel, useOrgModerationStatusLabel } from '@/lib/labels';
import { DetailGate } from '@/components/admin/detail-gate';
import { useOrganizationQuery } from '@/lib/queries';
import { organizationStatusTone } from '@/lib/status';
import { Card } from '@/components/ui/card';
import { VerificationSection } from './organization-verification-section';
import { MembersSection } from './organization-members-section';

const LIST_PATH = '/organizations';

/** A counter the API may not send yet: the number, or a dash. */
function countOrDash(value: number | undefined): string {
  return value === undefined ? '—' : formatCount(value);
}

/**
 * One club, federation or sports organization: its official identity, the registration
 * behind its badge, what it does on the platform, and who works in it.
 *
 * The verification block, the activity counters and most member fields are new on the
 * API; against today's response the screen shows the identity, the badge and the roster
 * (name, role, department, status), and dashes for the rest.
 */
export function OrganizationDetailRoute() {
  const organizationId = useParams().organizationId ?? '';
  const t = useTranslations('admin.organizationDetail');
  const location = useLocation();
  const backTo = listReturnPath(location.state, LIST_PATH);

  const organization = useOrganizationQuery(organizationId);

  return (
    <DetailGate
      query={organization}
      notFound={{
        title: t('notFound.title'),
        hint: t('notFound.hint'),
        backTo,
        backLabel: t('back'),
      }}
    >
      {(data) => <OrganizationDetail organization={data} backTo={backTo} />}
    </DetailGate>
  );
}

function OrganizationDetail({
  organization: org,
  backTo,
}: {
  organization: AdminOrganizationDetail;
  backTo: string;
}) {
  const t = useTranslations('admin.organizationDetail');
  const orgs = useTranslations('admin.organizations');
  const common = useTranslations('common');
  const kindLabel = useOrganizationKindLabel();
  const statusLabel = useOrgModerationStatusLabel();
  const cityLabel = useCityLabel();

  return (
    <div className="space-y-6">
      <BackLink to={backTo} label={t('back')} />

      <Card asChild>
        <header className="p-5 shadow-sm">
          <div className="flex flex-wrap items-start justify-between gap-3">
            <div className="min-w-0">
              <h2 className="flex items-center gap-1.5 text-lg font-bold text-navy" dir="auto">
                {org.name}
                {org.verificationStatus === 'verified' && (
                  <span title={orgs('verifiedAccount')}>
                    <Shield aria-hidden className="h-4 w-4 text-gold-700" />
                  </span>
                )}
              </h2>
              <p className="mt-0.5 text-[12.5px] text-conc-500">
                {kindLabel(org.kind)}
                {cityLabel(org.city) && ` · ${cityLabel(org.city)}`}
                {' · '}
                <span className="num">
                  {t('registered', { date: isoDate(org.createdAt) ?? '' })}
                </span>
              </p>
            </div>
            <StatusBadge tone={organizationStatusTone(org.verificationStatus)}>
              {statusLabel(org.verificationStatus)}
            </StatusBadge>
          </div>
        </header>
      </Card>

      <div className="grid gap-6 xl:grid-cols-2">
        <DetailSection title={t('section.identity')}>
          <FieldGrid>
            <Field label={orgs('officialEmail')} className="sm:col-span-2">
              {org.officialEmail && (
                <ContactValue value={org.officialEmail} copyLabel={common('copyEmail')} />
              )}
            </Field>
            <Field label={t('field.cr')}>
              {org.commercialRegistration && (
                <ContactValue value={org.commercialRegistration} copyLabel={t('copyCr')} numeric />
              )}
            </Field>
            <Field label={t('field.kind')}>{kindLabel(org.kind)}</Field>
            <Field label={t('field.city')}>{cityLabel(org.city)}</Field>
            <Field label={t('field.verifiedAt')}>
              {org.verifiedAt && <span className="num">{isoDate(org.verifiedAt)}</span>}
            </Field>
          </FieldGrid>
        </DetailSection>

        <VerificationSection organization={org} />
      </div>

      <DetailSection title={t('section.activity')}>
        <div className="grid grid-cols-2 gap-2 sm:grid-cols-3 xl:grid-cols-6">
          <StatTile label={t('stats.members')} value={formatCount(org.memberCount)} />
          <StatTile label={t('stats.vacancies')} value={countOrDash(org.vacancyCount)} />
          <StatTile label={t('stats.openVacancies')} value={countOrDash(org.openVacancyCount)} />
          <StatTile label={t('stats.shortlist')} value={countOrDash(org.shortlistCount)} />
          <StatTile
            label={t('stats.recommendations')}
            value={countOrDash(org.recommendationCount)}
          />
          <StatTile label={t('stats.profilesViewed')} value={countOrDash(org.profileViewsGiven)} />
        </div>
      </DetailSection>

      <MembersSection members={org.members} />
    </div>
  );
}
